if (!customElements.get('zoom-dialog')) {
  class ZoomDialog extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('dialog');
      this.swiperComponent = this.querySelector('.dialog-zoomed-gallery-swiper');
      this.gallery = this.querySelector('.dialog-zoomed-gallery');
      this.media = Array.from(this.querySelectorAll('.dialog-zoomed-gallery .product-media-container'));
      this.thumbnails = this.querySelector('.dialog-thumbnails-list');
      this.tabButtons = Array.from(this.querySelectorAll('[data-zoom-tab]'));
      this.activeType = this.tabButtons.find(button => button.getAttribute('aria-selected') === 'true')?.dataset.zoomTab || 'image';
      this.highResImagesLoaded = new Set();
      this.swiperEventsBound = false;

      this.handleScroll = this.debounce(this.handleScrollEvent.bind(this), 50);
      this.handleDialogClick = this.handleDialogClick.bind(this);

      if (this.gallery) {
        this.gallery.addEventListener('scroll', this.handleScroll);
      }
    }

    connectedCallback() {
      this.querySelectorAll('[data-zoom-index]').forEach(trigger => {
        trigger.addEventListener('click', e => {
          if (trigger.classList.contains('dialog-thumbnails-list__thumbnail')) return;
          if (trigger.closest('.dialog-zoomed-gallery')) return;

          const index = Number(trigger.dataset.zoomIndex);

          this.open(index, e);
        });
      });

      this.tabButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.setActiveType(button.dataset.zoomTab);
        });
      });

      this.bindSwiperEvents();

      this.dialog?.addEventListener('click', this.handleDialogClick);
    }

    disconnectedCallback() {
      if (this.gallery) {
        this.gallery.removeEventListener('scroll', this.handleScroll);
      }

      this.dialog?.removeEventListener('click', this.handleDialogClick);
    }

    async open(index, event) {
      event.preventDefault();

      const trigger = event.target instanceof Element ? event.target.closest('[data-zoom-index]') : null;
      const targetImage = this.getMediaByTrigger(trigger) || this.media[index];
      const activeIndex = this.getMediaIndex(targetImage);
      const targetThumbnail = this.getThumbnail(activeIndex);
      const activeType = targetImage?.dataset.mediaType || trigger?.dataset.mediaType || this.activeType;

      const openDialog = () => {
        if (this.dialog.showModal) {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        this.setActiveType(activeType, { selectFirst: false });
        if (!this.getSwiper()) this.swiperComponent?.initializeSwiper?.();
        this.bindSwiperEvents();
        this.getSwiper()?.update?.();

        targetThumbnail?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });

        if (this.getSwiper()) {
          this.slideTo(activeIndex, 'auto');
        } else {
          targetImage?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
        }
      };

      const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

      if (!this.supportsViewTransitions() || !sourceImage || !targetImage) {
        openDialog();
        this.selectThumbnail(activeIndex, { behavior: 'auto' });
        return;
      }

      const transitionName = `gallery-item`;
      sourceImage.style.setProperty('view-transition-name', transitionName);

      await this.startViewTransition(() => {
        openDialog();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', transitionName);
      });

      targetImage.style.removeProperty('view-transition-name');
      this.selectThumbnail(activeIndex, { behavior: 'auto' });
    }

    closeDialog() {
      if (this.dialog.close) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      window.dispatchEvent(new CustomEvent('dialog-close'));
    }

    handleDialogClick(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('.dialog-media-layout')) return;

      this.closeDialog();
    }

    async close() {
      if (!this.supportsViewTransitions()) return this.closeDialog();

      const activeIndex = this.getActiveIndex();
      const mostVisibleElement = this.media[activeIndex] || this.media[0];
      if (!mostVisibleElement) return this.closeDialog();
      const transitionName = `gallery-item`;

      const mediaGallery = this.closest('media-gallery');
      const slideshowActive = mediaGallery?.presentation === 'carousel';
      const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

      if (!slide) return this.closeDialog();

      this.dialog.classList.add('dialog--closed');
      await this.onAnimationEnd(this.thumbnails);

      mostVisibleElement.style.setProperty('view-transition-name', transitionName);

      await this.startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', transitionName);
        this.closeDialog();
      });

      slide.style.removeProperty('view-transition-name');
      this.dialog.classList.remove('dialog--closed');
    }

    async handleScrollEvent() {
      const activeIndex = this.getActiveIndex();
      const mostVisibleElement = this.media[activeIndex];
      if (!mostVisibleElement) return;
      const targetThumbnail = this.getThumbnail(activeIndex);

      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      Array.from(this.thumbnails.querySelectorAll('button')).forEach((button) => {
        button.setAttribute('aria-selected', `${Number(button.dataset.zoomIndex) === activeIndex}`);
      });

      this.loadHighResolutionImage(mostVisibleElement);
      this.dispatchEvent(new CustomEvent('zoom-media:selected', { detail: { index: activeIndex } }));
    }

    async handleThumbnailClick(index) {
      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
      this.selectThumbnail(index, { behavior });
    }

    async selectThumbnail(index, options = { behavior: 'smooth', syncSwiper: true }) {
      if (isNaN(index) || index < 0 || index >= this.media.length) return;

      const targetImage = this.media[index];
      if (!targetImage || !(targetImage instanceof HTMLElement)) return;

      const targetType = targetImage.dataset.mediaType || 'image';
      if (targetType !== this.activeType) {
        this.setActiveType(targetType, { selectFirst: false });
      }

      const targetThumbnail = this.getThumbnail(index);
      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      Array.from(this.thumbnails.querySelectorAll('button')).forEach((button) => {
        button.setAttribute('aria-selected', `${Number(button.dataset.zoomIndex) === index}`);
      });

      this.scrollIntoView(targetThumbnail, {
        ancestor: this.thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });

      if (this.getSwiper()) {
        if (options.syncSwiper !== false) {
          this.slideTo(index, options.behavior);
        }
      } else {
        targetImage.scrollIntoView({
          behavior: options.behavior,
          block: 'center',
          inline: 'center',
        });
      }

      this.loadHighResolutionImage(targetImage);

      this.dispatchEvent(new CustomEvent('zoom-media-selected', { detail: { index } }));
    }

    setActiveType(type, options = { selectFirst: true }) {
      if (!type) return;

      const availableThumbnails = this.getThumbnailsByType(type);

      this.activeType = type;

      this.tabButtons.forEach(button => {
        button.setAttribute('aria-selected', `${button.dataset.zoomTab === type}`);
      });

      Array.from(this.thumbnails?.querySelectorAll('button') || []).forEach(button => {
        button.hidden = button.dataset.mediaType !== type;
      });

      const selectedMedia = this.media[this.getActiveIndex()];
      const selectedType = selectedMedia?.dataset.mediaType;

      if (!availableThumbnails.length) return;

      if (options.selectFirst || selectedType !== type) {
        const firstIndex = Number(availableThumbnails[0].dataset.zoomIndex);
        this.selectThumbnail(firstIndex, { behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    getMediaByTrigger(trigger) {
      if (!trigger) return null;

      const mediaId = trigger.dataset.zoomMediaId;
      if (mediaId) {
        return this.media.find(media => media.dataset.zoomMediaId === mediaId) || null;
      }

      const index = Number(trigger.dataset.zoomIndex);
      return Number.isNaN(index) ? null : this.media[index] || null;
    }

    getMediaIndex(media) {
      const index = this.media.indexOf(media);
      return index >= 0 ? index : -1;
    }

    getThumbnail(index) {
      return this.thumbnails?.querySelector(`[data-zoom-index="${index}"]`) || null;
    }

    getThumbnailsByType(type) {
      return Array.from(this.thumbnails?.querySelectorAll(`[data-media-type="${type}"]`) || []);
    }

    bindSwiperEvents(retries = 10) {
      if (this.swiperEventsBound) return;

      const swiper = this.getSwiper();
      if (!swiper) {
        if (retries > 0) setTimeout(() => this.bindSwiperEvents(retries - 1), 50);
        return;
      }

      swiper.on('slideChange', () => {
        const index = this.getActiveIndex();
        this.selectThumbnail(index, { behavior: 'auto', syncSwiper: false });
      });

      this.swiperEventsBound = true;
    }

    getSwiper() {
      return this.swiperComponent?.initSwiper || this.gallery?.swiper || null;
    }

    getActiveIndex() {
      const swiper = this.getSwiper();
      if (swiper && typeof swiper.realIndex === 'number') return swiper.realIndex;
      if (swiper && typeof swiper.activeIndex === 'number') return swiper.activeIndex;

      const selectedThumbnail = this.thumbnails?.querySelector('[aria-selected="true"]');
      const selectedIndex = Number(selectedThumbnail?.dataset.zoomIndex);
      return Number.isNaN(selectedIndex) ? 0 : selectedIndex;
    }

    slideTo(index, behavior = 'smooth', retries = 10) {
      const swiper = this.getSwiper();
      if (!swiper) {
        if (retries > 0) setTimeout(() => this.slideTo(index, behavior, retries - 1), 50);
        return;
      }

      const speed = behavior === 'auto' || this.prefersReducedMotion() ? 0 : 500;
      swiper.slideTo(index, speed);
    }

    getVisibleMedia() {
      return this.media.filter(media => !media.hidden);
    }

    loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer.classList.contains('product-media-container--image')) return false;

      const image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return false;

      const highResolutionUrl = image.getAttribute('data_max_resolution');
      if (!highResolutionUrl || this.highResImagesLoaded.has(highResolutionUrl)) return false;

      this.preloadImage(highResolutionUrl);

      const newImage = new Image();
      newImage.className = image.className;
      newImage.alt = image.alt;
      newImage.setAttribute('data_max_resolution', highResolutionUrl);

      newImage.onload = () => {
        image.replaceWith(newImage);
        this.highResImagesLoaded.add(highResolutionUrl);
      };

      newImage.src = highResolutionUrl;
    }

    getMostVisibleElement(elements) {
      return new Promise((resolve) => {
        const observer = new IntersectionObserver(
          (entries) => {
            const mostVisible = entries.reduce((prev, current) =>
              current.intersectionRatio > prev.intersectionRatio ? current : prev
            );
            observer.disconnect();
            resolve(mostVisible.target);
          },
          { threshold: [0, 0.5, 1] }
        );
        for (const element of elements) {
          observer.observe(element);
        }
      });
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    supportsViewTransitions() {
      return 'startViewTransition' in document;
    }

    async startViewTransition(callback) {
      if (this.supportsViewTransitions()) {
        return document.startViewTransition(callback);
      } else {
        callback();
      }
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    preloadImage(src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    }

    onAnimationEnd(element) {
      return new Promise((resolve) => {
        const handleAnimationEnd = () => {
          element.removeEventListener('animationend', handleAnimationEnd);
          resolve();
        };
        element.addEventListener('animationend', handleAnimationEnd);
      });
    }

    scrollIntoView(element, { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}) {
      if (!ancestor) {
        return element.scrollIntoView({ behavior, block, inline });
      }

      const ancestorRect = ancestor.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
        switch (alignment) {
          case 'start':
            return currentScroll + elemStart - ancestorStart;
          case 'center':
            return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
          case 'end':
            return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
          default:
            return currentScroll;
        }
      };

      const scrollTop =
        ancestor.scrollHeight > ancestor.clientHeight
          ? calculateScrollOffset(
              block,
              ancestorRect.top,
              ancestor.clientHeight,
              elementRect.top,
              elementRect.height,
              ancestor.scrollTop
          )
          : ancestor.scrollTop;

      const scrollLeft =
        ancestor.scrollWidth > ancestor.clientWidth
          ? Math.max(0, calculateScrollOffset(
              inline,
              ancestorRect.left,
              ancestor.clientWidth,
              elementRect.left,
              elementRect.width,
              ancestor.scrollLeft
            ))
          : ancestor.scrollLeft;

      ancestor.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: behavior
      });
    }
  }

  if (!customElements.get('zoom-dialog')) {
    customElements.define('zoom-dialog', ZoomDialog);
  }
}

document.addEventListener('click', e => {
  const trigger = e.target.closest('[data-zoom-target]');
  if (!trigger) return;

  const targetSelector = trigger.dataset.zoomTarget;
  const zoomDialog = document.querySelector(targetSelector);
  if (!zoomDialog) return;

  const index = Number(trigger.dataset.zoomIndex);

  if (trigger.classList.contains('product-media-container--zoomable')) {
    zoomDialog.open(index, e);
  }

  if (trigger.classList.contains('dialog-thumbnails-list__thumbnail')) {
    e.preventDefault();
    e.stopPropagation();
    zoomDialog.handleThumbnailClick(index);
  }
});

if (!customElements.get('scroll-hint')) {
  class ScrollHint extends HTMLElement {
    connectedCallback() {
      this.addEventListener('scroll', theme.utils.rafThrottle(this.update.bind(this)));
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.update);
      this.resizeObserver.disconnect();
    }

    update = () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = this;
      const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
      const scrollPercentage =
        scrollDirection === 'vertical'
          ? scrollTop / (scrollHeight - clientHeight)
          : scrollLeft / (scrollWidth - clientWidth);

      this.style.maskImage = Number.isNaN(scrollPercentage)
        ? ''
        : `linear-gradient(
          to ${scrollDirection === 'vertical' ? 'bottom' : 'right'},
          transparent ${scrollPercentage > 0 ? 1 : 0}%,
          black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
          black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
          transparent 100%
        )`;
    };

    resizeObserver = new ResizeObserver(this.update);
  }

  customElements.define('scroll-hint', ScrollHint);
}
